import { useCallback, useEffect, useState } from "react";
import { useAtomValue } from "jotai";
import { Link } from "react-router-dom";
import { apiCall } from "../../lib/api-client.js";
import { ApiError, type Organization } from "../../lib/api-types.js";
import { authAtom } from "../../lib/auth-store.js";
import { adminOrgPath } from "../../lib/paths.js";
import { PageHeader } from "../../components/PageHeader.js";
import { InlineLoading } from "../../components/Spinner.js";
import { Empty } from "../../components/Empty.js";
import { ErrorBanner } from "../../components/ErrorBanner.js";
import { StatusPill } from "../../components/StatusPill.js";
import { OrganizationCreateDialog } from "./OrganizationCreateDialog.js";
import { formatDate } from "../../lib/format.js";

export function OrganizationList(): JSX.Element {
  const auth = useAtomValue(authAtom);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [orgs, setOrgs] = useState<Organization[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const load = useCallback(async () => {
    if (!auth) return;
    try {
      const qs = includeArchived ? "?" + new URLSearchParams({ includeArchived: "true" }).toString() : "";
      const data = await apiCall<Organization[]>("GET", `/api/organizations${qs}`, {
        token: auth.token,
      });
      setOrgs(data);
      setError(null);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : "无法加载组织列表";
      setError(msg);
    }
  }, [auth, includeArchived]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleCreated = (created: Organization): void => {
    setOrgs((prev) => (prev ? [created, ...prev] : [created]));
  };

  return (
    <div>
      <PageHeader
        title="组织"
        subtitle="超管视角 — 创建 / 状态切换 / 归档"
        actions={
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            className="rounded bg-dosia-accent px-3 py-1.5 text-sm font-medium text-dosia-bg-light hover:bg-dosia-accent-hover"
          >
            创建组织
          </button>
        }
      />

      <div className="flex items-center justify-between pb-3">
        <label className="inline-flex items-center gap-2 text-sm text-dosia-text-secondary">
          <input
            type="checkbox"
            checked={includeArchived}
            onChange={(e) => setIncludeArchived(e.target.checked)}
            className="rounded border-dosia-border"
          />
          显示已归档
        </label>
      </div>

      {error ? (
        <div className="mb-4">
          <ErrorBanner onDismiss={() => setError(null)}>{error}</ErrorBanner>
        </div>
      ) : null}

      {orgs === null ? (
        <InlineLoading />
      ) : orgs.length === 0 ? (
        <Empty
          title={includeArchived ? "暂无组织" : "暂无活跃组织"}
          description="点右上方「创建组织」开始。"
        />
      ) : (
        <div className="overflow-hidden rounded-lg border border-dosia-border bg-dosia-bg-light">
          <table className="w-full text-sm">
            <thead className="bg-dosia-bg-lighter text-left text-xs uppercase tracking-wide text-dosia-text-secondary">
              <tr>
                <th className="px-3 py-2 font-medium">代号</th>
                <th className="px-3 py-2 font-medium">名称</th>
                <th className="px-3 py-2 font-medium">状态</th>
                <th className="px-3 py-2 font-medium">飞书</th>
                <th className="px-3 py-2 font-medium">创建时间</th>
                <th className="px-3 py-2 font-medium" />
              </tr>
            </thead>
            <tbody className="divide-y divide-dosia-border-light">
              {orgs.map((o) => (
                <tr key={o.id} className="hover:bg-dosia-bg-lighter/60">
                  <td className="px-3 py-2 font-mono text-dosia-text">{o.code}</td>
                  <td className="px-3 py-2 text-dosia-text">{o.name}</td>
                  <td className="px-3 py-2">
                    <StatusPill status={o.status} />
                  </td>
                  <td className="px-3 py-2">
                    <StatusPill
                      status={o.feishu?.appId ? "connected" : "disconnected"}
                    />
                  </td>
                  <td className="px-3 py-2 text-dosia-text-secondary">
                    {formatDate(o.createdAt)}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Link
                      to={adminOrgPath(o.code)}
                      className="text-dosia-accent-700 hover:underline"
                    >
                      详情
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <OrganizationCreateDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={handleCreated}
      />
    </div>
  );
}
